import { Injectable } from '@angular/core';
import { TransactionEVDto } from '../../models/ev.model';
import {
  EvChargerDashboardStats,
  EvChargerDonutStats,
  EvChargerDonutStatsDto,
  EvChargerSummaryDto,
  EvChargerTransactionChartDto,
  EvChargerTransactionChartItemDto,
  EvChargerTransactionTableDto,
} from '../../models/ev/ev-charger-list.model';

@Injectable({
  providedIn: 'root',
})
export class MapperEvChargerListService {
  mapTableFromDto(dto: EvChargerTransactionTableDto): {
    items: TransactionEVDto[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    summary: { dashboard: EvChargerDashboardStats; donut: EvChargerDonutStats } | null;
  } {
    return {
      items: (dto.data ?? []).map(item => this.normalizeTransactionItem(item)),
      total: dto.total ?? 0,
      page: dto.page ?? 1,
      pageSize: dto.pageSize ?? 20,
      totalPages: dto.totalPages ?? 0,
      summary: dto.summary ? this.mapSummary(dto.summary) : null,
    };
  }

  mapChartItems(dto: EvChargerTransactionChartDto): EvChargerTransactionChartItemDto[] {
    return (dto.items ?? []).map(item => ({
      id: item.id ?? (item as any).Id ?? 0,
      enterAt: item.enterAt ?? (item as any).EnterAt ?? null,
      licensePlate: item.licensePlate ?? (item as any).LicensePlate ?? '',
      price: item.price ?? (item as any).Price ?? 0,
    }));
  }

  mapSearchField(type: 'licensePlate' | 'siteName' | 'spaceCode'): string {
    switch (type) {
      case 'siteName':
        return 'siteName';
      case 'spaceCode':
        return 'spaceCode';
      default:
        return 'licensePlate';
    }
  }

  mapSortColumn(column: string): string {
    return column.toLowerCase();
  }

  mapStatusCodes(filterStatus: Record<number, boolean>): string | undefined {
    const selected = Object.entries(filterStatus)
      .filter(([, checked]) => checked)
      .map(([code]) => code);

    return selected.length > 0 ? selected.join(',') : undefined;
  }

  mapExitFilter(showNotExitedOnly: boolean, showExitedOnly: boolean): 'notExited' | 'exited' | undefined {
    if (showNotExitedOnly && !showExitedOnly) return 'notExited';
    if (showExitedOnly && !showNotExitedOnly) return 'exited';
    return undefined;
  }

  private mapSummary(summary: EvChargerSummaryDto): {
    dashboard: EvChargerDashboardStats;
    donut: EvChargerDonutStats;
  } {
    const raw = summary as EvChargerSummaryDto & Record<string, unknown>;
    const donutRaw = summary.donutStats ?? raw['DonutStats'];
    return {
      dashboard: {
        totalTransactions: summary.totalTransactions ?? (raw['TotalTransactions'] as number) ?? 0,
        waiting: summary.waiting ?? (raw['Waiting'] as number) ?? 0,
        activeCharging: summary.activeCharging ?? (raw['ActiveCharging'] as number) ?? 0,
        completed: summary.completed ?? (raw['Completed'] as number) ?? 0,
        totalRevenue: summary.totalRevenue ?? (raw['TotalRevenue'] as number) ?? 0,
      },
      donut: this.normalizeDonutStats(donutRaw as EvChargerDonutStatsDto),
    };
  }

  private normalizeDonutStats(raw: EvChargerDonutStatsDto | null | undefined): EvChargerDonutStats {
    const source = raw ?? ({} as EvChargerDonutStatsDto);
    return {
      totalTransactions: source.totalTransactions ?? (source as any).TotalTransactions ?? 0,
      waiting: source.waiting ?? (source as any).Waiting ?? 0,
      activeCharging: source.activeCharging ?? (source as any).ActiveCharging ?? 0,
      completed: source.completed ?? (source as any).Completed ?? 0,
    };
  }

  private normalizeTransactionItem(raw: TransactionEVDto): TransactionEVDto {
    return {
      id: raw.id ?? (raw as any).Id,
      activate: raw.activate ?? (raw as any).Activate ?? false,
      historyUuid: raw.historyUuid ?? (raw as any).HistoryUuid ?? '',
      licensePlate: raw.licensePlate ?? (raw as any).LicensePlate ?? '',
      spaceCode: raw.spaceCode ?? (raw as any).SpaceCode ?? '',
      enterAt: this.toDate(raw.enterAt ?? (raw as any).EnterAt),
      exitAt: this.toDate(raw.exitAt ?? (raw as any).ExitAt),
      totalTime: raw.totalTime ?? (raw as any).TotalTime ?? 0,
      siteEv: raw.siteEv ?? (raw as any).SiteEv ?? '',
      siteId: raw.siteId ?? (raw as any).SiteId ?? 0,
      siteName: raw.siteName ?? (raw as any).SiteName ?? '',
      price: raw.price ?? (raw as any).Price ?? 0,
      jobCount: raw.jobCount ?? (raw as any).JobCount ?? 0,
      status: raw.status ?? (raw as any).Status ?? 0,
      isReady: raw.isReady ?? (raw as any).IsReady ?? false,
    };
  }

  private toDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}
